import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Popconfirm, message } from '@myua/kit';
import { useDispatch, useSelector } from 'react-redux';
import { Asset } from '../../../api';
import { getAssetLabLogger } from '../../app/logs';
import { getData } from '../../finder/selectors';
import { getUser } from '../../app/selectors';
import * as magicStudioActions from '../actions';
import { getPrimaryAsset, getSession, getSessionRefs, getVisible } from '../selectors';
import {
  Aspect,
  composePrompt,
  CUSTOM_TEMPLATE_ID,
  dataUrlByteSize,
  DEFAULT_ASPECT,
  DEFAULT_MODEL_ID,
  DEFAULT_RESOLUTION,
  MagicRef,
  MagicResult,
  MagicSession,
  MagicTemplate,
  MAX_REFS,
  ModelSelection,
  Resolution,
  aiMetaFor,
  assetBaseName,
  assetRef,
  modelResolutions,
  modelSelections,
  resolveResolution,
  sameSelection,
  uploadRef,
} from '../model';
import { useMagicHistory, MagicGenerationView } from '../history/useMagicHistory';
import { SavedAssetLink } from '../saveToAssets/types';
import { getApiKey } from '../apiKey';
import { loadTemplates, saveTemplates } from '../templatesStore';
import { loadPrompts, rememberPrompt } from '../promptHistory';
import { useAiKeys } from '../useAiKeys';
import { withImageExtension } from '../imageFormat';
import { resolveBackendRefs, resolveRefs } from '../imageResolver';
import { MAGIC_BACKEND_ENABLED } from '../providers/config';
import { disposeEngine, runLocalOp as runEngineLocalOp } from '../localOps/engine';
import type { LocalOpKind, LocalOpState } from '../localOps/types';
import { isInstalled, markInstalled } from '../localOps/install';
import { blobToDataUrl, downloadImageUrl } from '../localOps/download';
import { generationPath, getProvider, isRealGeneration } from '../providers/registry';
import { MagicProviderError } from '../providers/types';
import { TM } from '../tokens';
import { CloseIcon, HistoryIcon, MagicIcon, RefreshIcon } from '../icons';
import { MagicBtn, StudioTab } from './ui';
import { MagicHistory } from './MagicHistory';
import { SaveToAssetsButton } from './SaveToAssetsButton';
import { RefsPanel } from './RefsPanel';
import { CanvasPanel } from './CanvasPanel';
import { TemplatesPanel } from './TemplatesPanel';
import { TemplateEditor } from './TemplateEditor';
import { AssetPicker } from './AssetPicker';
import '../styles.scss';

// Remembers the last (key, model) the user generated with, so reopening the
// studio lands on it instead of resetting to whatever the backend listed first.
// Only ids — no secret ever reaches localStorage.
const LAST_SELECTION_KEY = 'assetLab.magicStudio.lastModel';

// Backend contract: POST /ai/generations/ rejects a prompt shorter than this.
const MIN_PROMPT_LENGTH = 4;

type StoredSelection = { apiKeyId: number; model: string };

function readLastSelection(): StoredSelection | null {
  try {
    const raw = localStorage.getItem(LAST_SELECTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSelection>;
    return typeof parsed?.apiKeyId === 'number' && typeof parsed?.model === 'string'
      ? { apiKeyId: parsed.apiKeyId, model: parsed.model }
      : null;
  } catch {
    // Unavailable (private mode) or corrupted — fall back to the first option.
    return null;
  }
}

function writeLastSelection(selection: ModelSelection): void {
  try {
    localStorage.setItem(
      LAST_SELECTION_KEY,
      JSON.stringify({ apiKeyId: selection.apiKeyId, model: selection.model }),
    );
  } catch {
    // ignore — remembering the choice is a convenience, not a requirement.
  }
}

// Magic Studio — the modal opened by the magic button. Generation with
// references from Asset Lab, running through the asset_lab AI proxy (the user's
// provider key is registered server-side; see providers/backendProvider.ts).
export const MagicStudioModal: React.FC = () => {
  const dispatch = useDispatch();
  const visible = useSelector(getVisible);
  // The session is opened with a snapshot of references (the basket, or empty).
  // The first one is the primary "style anchor".
  const sessionRefs = useSelector(getSessionRefs);
  // Work stashed by the previous close — restored on open so an accidental ✕
  // costs nothing. Null on a first open and after "New session" / tray Clear.
  const storedSession = useSelector(getSession);
  const primaryAsset = useSelector(getPrimaryAsset);
  const allAssets = useSelector(getData).assets;
  const assetLabLogger = useSelector(getAssetLabLogger);
  const user = useSelector(getUser);
  // Namespace history by the logged-in user so accounts on a shared browser
  // don't see each other's generations. '' when the user is unknown.
  const userId = user ? String(user.id) : '';

  // Generate | History switcher. History auto-loads from local storage while
  // the studio is open (see useMagicHistory).
  const [tab, setTab] = useState<'generate' | 'history'>('generate');
  const { history, addGeneration, markGenerationSaved, deleteGeneration } = useMagicHistory(
    userId,
    {
      enabled: visible,
    },
  );

  // Built-in templates plus the user's own / edited ones, restored from this
  // device (see templatesStore.ts) — reloaded on every open so a change made in
  // another tab is picked up.
  const [templates, setTemplates] = useState<MagicTemplate[]>(() => loadTemplates(userId));
  const [activeTpl, setActiveTpl] = useState(CUSTOM_TEMPLATE_ID);
  const [model, setModel] = useState(DEFAULT_MODEL_ID);
  const [aspect, setAspect] = useState<Aspect>(DEFAULT_ASPECT);
  // Target output resolution (Gemini imageConfig.imageSize); kept valid for the
  // active model. Empty for models without a size choice.
  const [resolution, setResolution] = useState<Resolution>(() =>
    resolveResolution(DEFAULT_MODEL_ID, DEFAULT_RESOLUTION),
  );
  const [prompt, setPrompt] = useState('');
  // Prompts this user generated with before, newest first — the source for the
  // prompt field's autocomplete. Re-read on every open so a prompt written in
  // another tab is offered here too.
  const [promptHistory, setPromptHistory] = useState<string[]>(() => loadPrompts(userId));
  const [refs, setRefs] = useState<MagicRef[]>([]);
  const [picking, setPicking] = useState(false);
  const [result, setResult] = useState<MagicResult | null>(null);
  const [generating, setGenerating] = useState(false);
  // Last generation error, shown in the canvas preview area (e.g. Gemini's
  // finishMessage when it finishes without an image). Cleared on each new run.
  const [genError, setGenError] = useState<string | null>(null);
  // Set when Generate is pressed with nothing to generate with — the canvas
  // shows the large "connect a key" screen instead of silently doing nothing.
  const [comingSoon, setComingSoon] = useState(false);
  // An on-device tool (background removal / upscale) in progress. Runs locally
  // in a worker; independent of the API key, so it works even in Demo mode.
  const [localOp, setLocalOp] = useState<LocalOpState | null>(null);
  const [editingTpl, setEditingTpl] = useState<MagicTemplate | null>(null);

  // ─── Keys & model selection ────────────────────────────────────────
  // Backend path: the keys the user registered server-side, loaded while the
  // modal is open. They also carry the models each key can serve, so the model
  // picker is built from them (a bare model isn't enough — the backend derives
  // the provider from the key).
  const {
    keys,
    loading: keysLoading,
    error: keysError,
    reload: reloadKeys,
    refresh: refreshKeys,
    addKey,
    removeKey,
  } = useAiKeys(MAGIC_BACKEND_ENABLED && visible);
  const modelOptions = useMemo(() => modelSelections(keys), [keys]);
  const [selection, setSelection] = useState<ModelSelection | null>(null);

  // Direct path (legacy): the user's own Google AI Studio key from localStorage,
  // used only while MAGIC_BACKEND_ENABLED is false.
  const [directApiKey, setDirectApiKey] = useState<string | null>(() =>
    MAGIC_BACKEND_ENABLED ? null : getApiKey(),
  );
  const apiKey = MAGIC_BACKEND_ENABLED ? null : directApiKey;

  // Keep the selection valid as keys arrive / change: keep the current one if it
  // still exists, otherwise restore the remembered pair, otherwise take the
  // first option. Removing the selected key therefore moves the studio onto
  // another one instead of leaving a dangling api_key_id.
  useEffect(() => {
    if (!MAGIC_BACKEND_ENABLED) return;
    setSelection((prev) => {
      if (prev && modelOptions.some((o) => sameSelection(o, prev))) return prev;
      if (modelOptions.length === 0) return null;
      const last = readLastSelection();
      const remembered =
        last && modelOptions.find((o) => o.apiKeyId === last.apiKeyId && o.model === last.model);
      return remembered ?? modelOptions[0];
    });
  }, [modelOptions]);

  const pickSelection = useCallback((next: ModelSelection) => {
    setSelection(next);
    writeLastSelection(next);
  }, []);

  // The model string every downstream consumer sees — history records, the saved
  // asset's name and the XMP metadata. On the backend path that is the technical
  // model name ('gpt-image-1'); on the direct path the legacy studio id ('nb').
  // modelName/modelSoftware understand both, so old history keeps rendering.
  const activeModelId = MAGIC_BACKEND_ENABLED ? selection?.model ?? model : model;

  // Provider of the selected key — decides whether the active model offers an
  // output resolution (Gemini does, OpenAI doesn't) when the model itself isn't
  // in MODEL_CATALOG. Undefined on the direct path: there the model is a legacy
  // id carrying its own resolution list.
  const activeProvider = MAGIC_BACKEND_ENABLED ? selection?.provider : undefined;

  // Budget left for this user on the selected key — shown in the footer so the
  // spend is visible before it runs out. Null when nothing is selected.
  const selectedKeyRemaining =
    keys.find((k) => k.id === selection?.apiKeyId)?.user_remaining_usd ?? null;

  // Object URLs created for uploaded/pasted references — tracked so they can
  // be revoked to avoid leaks when removed, reset or unmounted.
  const objectUrlsRef = useRef<Set<string>>(new Set());

  // Bumped on every (re)open and every generate; in-flight fake generations
  // compare against it so a stale response can't bleed into a new session.
  const genIdRef = useRef(0);

  // Put the studio into a given session: the stashed one on reopen, or a blank
  // one (`session` null) for a fresh start. Everything that belongs to a single
  // visit — generating, errors, the on-device progress, the open sub-dialogs —
  // is reset either way; only what the user authored is carried over.
  const applySession = useCallback(
    (session: MagicSession | null, nextRefs: MagicRef[]) => {
      // Supersede any in-flight generation / local op, so a late response can't
      // land in the session we're switching to.
      genIdRef.current += 1;
      // Free the object URLs of uploaded refs the new session doesn't use;
      // restored ones must stay alive, which is why this isn't a blanket revoke.
      const kept = new Set(nextRefs.filter((r) => r.kind === 'upload').map((r) => r.url));
      objectUrlsRef.current.forEach((url) => {
        if (kept.has(url)) return;
        URL.revokeObjectURL(url);
        objectUrlsRef.current.delete(url);
      });
      // Re-read on every open so a template / prompt written in another tab
      // shows up here too.
      const nextTemplates = loadTemplates(userId);
      setTemplates(nextTemplates);
      setPromptHistory(loadPrompts(userId));
      // A template deleted meanwhile (here or in another tab) falls back to the
      // neutral mode rather than leaving a badge pointing at nothing.
      setActiveTpl(
        session && nextTemplates.some((t) => t.id === session.templateId)
          ? session.templateId
          : CUSTOM_TEMPLATE_ID,
      );
      setModel(session?.model ?? DEFAULT_MODEL_ID);
      setAspect(session?.aspect ?? DEFAULT_ASPECT);
      setResolution(session?.resolution ?? resolveResolution(DEFAULT_MODEL_ID, DEFAULT_RESOLUTION));
      setPrompt(session?.prompt ?? '');
      setRefs(nextRefs);
      setResult(session?.result ?? null);
      setGenError(null);
      setComingSoon(false);
      setLocalOp(null);
      setGenerating(false);
      setPicking(false);
      setEditingTpl(null);
      setTab('generate');
    },
    [userId],
  );

  // Load the session whenever the studio opens — the stashed one plus the basket
  // as its references (the basket *is* what the last close wrote back, so this
  // resumes rather than resets). An empty basket with nothing stashed starts the
  // user clean, ready to paste / upload from scratch.
  //
  // It also runs while open when `sessionCleared` empties the store, which is
  // exactly what "New session" needs — one reset path instead of two.
  useEffect(() => {
    if (!visible) return;
    applySession(storedSession, sessionRefs);
  }, [visible, sessionRefs, storedSession, applySession]);

  // The live session, mirrored into a ref so the stash-on-close effect below can
  // read the latest values without re-subscribing on every keystroke.
  const liveSession = useRef<{ refs: MagicRef[]; session: MagicSession }>({
    refs: [],
    session: {
      prompt: '',
      templateId: CUSTOM_TEMPLATE_ID,
      templateLabel: '',
      model: DEFAULT_MODEL_ID,
      aspect: DEFAULT_ASPECT,
      resolution: DEFAULT_RESOLUTION,
      result: null,
    },
  });

  useEffect(() => {
    liveSession.current = {
      refs,
      session: {
        prompt,
        templateId: activeTpl,
        templateLabel: templates.find((t) => t.id === activeTpl)?.label ?? '',
        // The model actually in play: the technical name on the backend path,
        // the legacy studio id on the direct one (see activeModelId).
        model: activeModelId,
        aspect,
        resolution,
        result,
      },
    };
  });

  // Stash the session on every way out — the ✕, the footer button, Esc, the
  // backdrop, or a "reveal in Asset Lab" navigating away — by hanging it on the
  // cleanup of an effect that only lives while the studio is open. Closing is
  // therefore always a pause, never a discard; "New session" is the discard.
  useEffect(() => {
    if (!visible) return undefined;
    return () => {
      dispatch(magicStudioActions.sessionStashed(liveSession.current));
    };
  }, [visible, dispatch]);

  // Keep the chosen resolution valid when the model changes — switching to a
  // model that doesn't offer the current size falls back to its default. Keyed
  // on the active (model, provider), so it also follows a (key, model) switch on
  // the backend path, where the sizes come from the technical model name.
  useEffect(() => {
    setResolution((r) => resolveResolution(activeModelId, r, activeProvider));
  }, [activeModelId, activeProvider]);

  // Free the on-device worker (and the model it holds in memory) when the studio
  // closes; it's lazily re-created and reloads the model from cache next time.
  useEffect(() => {
    if (!visible) disposeEngine();
  }, [visible]);

  // Revoke any remaining object URLs on unmount, and tear down the worker.
  useEffect(() => {
    const urls = objectUrlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
      disposeEngine();
    };
  }, []);

  const close = () => dispatch(magicStudioActions.closed());

  // "New session" — the deliberate discard closing no longer does: references
  // (and the object URLs behind uploaded ones), prompt, result and output
  // settings all go. Resetting locally first keeps the paint clean; the action
  // then clears the store, which re-runs the restore effect on the same values.
  const newSession = () => {
    assetLabLogger({ action: 'Magic Studio', sub_action: 'New Session' });
    applySession(null, []);
    dispatch(magicStudioActions.sessionCleared());
  };

  // Whether there is anything for "New session" to throw away.
  const sessionDirty = refs.length > 0 || prompt.trim().length > 0 || !!result;

  // Add local images (from file picker, drag-drop or clipboard) as refs.
  const addFiles = useCallback((files: FileList | File[]) => {
    const images = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (!images.length) return;
    setRefs((prev) => {
      const room = MAX_REFS - prev.length;
      if (room <= 0) {
        void message.warning(`Up to ${MAX_REFS} references`);
        return prev;
      }
      const added = images.slice(0, room).map((f) => {
        const url = URL.createObjectURL(f);
        objectUrlsRef.current.add(url);
        return uploadRef(f.name || 'pasted-image.png', url);
      });
      return [...prev, ...added];
    });
  }, []);

  // Paste an image from the clipboard anywhere in the open studio.
  useEffect(() => {
    if (!visible) return;
    const onPaste = (e: ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.items ?? [])
        .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
        .map((it) => it.getAsFile())
        .filter((f): f is File => f !== null);
      if (files.length) {
        e.preventDefault();
        addFiles(files);
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [visible, addFiles]);

  // Picking a template only switches the active mode — its instruction becomes
  // the system prompt behind the badge; the prompt field (user content) is left
  // untouched.
  const pickTemplate = (tpl: MagicTemplate) => {
    setActiveTpl(tpl.id);
  };

  // Every template mutation goes through here so the list on screen and the
  // copy on this device never diverge.
  const commitTemplates = useCallback(
    (next: MagicTemplate[]) => {
      setTemplates(next);
      saveTemplates(userId, next);
    },
    [userId],
  );

  // Save an edited template — or append it, when it's a not-yet-added draft from
  // "New" (nothing is created until the user actually saves it).
  const saveTpl = (tpl: MagicTemplate) => {
    commitTemplates(
      templates.some((t) => t.id === tpl.id)
        ? templates.map((t) => (t.id === tpl.id ? tpl : t))
        : [...templates, tpl],
    );
    setEditingTpl(null);
  };

  // Badge action: fold the active template's system instruction into the prompt
  // field (at the top) and switch back to "No template", so it becomes editable
  // plain content.
  const resetTemplateToCustom = () => {
    const tpl = templates.find((t) => t.id === activeTpl);
    const system = tpl?.prompt.trim() ?? '';
    if (system) {
      setPrompt((p) => {
        const user = p.trim();
        return user ? `${system}\n\n${user}` : system;
      });
    }
    setActiveTpl(CUSTOM_TEMPLATE_ID);
  };

  // Append a modifier phrase to the user's prompt content.
  const addModifier = (text: string) => {
    setPrompt((p) => (p.trim() ? `${p.replace(/\s+$/, '')} ${text}` : text));
  };

  // "New" opens the editor on a draft; it only joins the list once saved, so
  // cancelling leaves no half-filled template behind (and none in storage).
  const addTpl = () => {
    setEditingTpl({
      id: `tpl_${Date.now()}`,
      label: 'New template',
      hint: '',
      prompt: '',
      presetRefs: ['primary'],
      custom: true,
    });
  };

  const removeTpl = (id: string) => {
    commitTemplates(templates.filter((t) => t.id !== id));
    if (activeTpl === id) {
      setActiveTpl(CUSTOM_TEMPLATE_ID);
    }
  };

  // Whether the template being edited is already in the list — a draft from
  // "New" isn't, so it has nothing to delete and gets the "New template" title.
  const editingExists = !!editingTpl && templates.some((t) => t.id === editingTpl.id);

  const addRef = (asset: Asset) => {
    const ref = assetRef(asset);
    setRefs((r) => (r.some((x) => x.key === ref.key) ? r : [...r, ref]));
    setPicking(false);
  };

  const removeRef = (key: string) =>
    setRefs((prev) => {
      const ref = prev.find((r) => r.key === key);
      if (ref?.kind === 'upload') {
        URL.revokeObjectURL(ref.url);
        objectUrlsRef.current.delete(ref.url);
      }
      return prev.filter((r) => r.key !== key);
    });

  // Reorder references by drag-and-drop. The first ref is the primary "style
  // anchor", so dragging a ref to the top promotes it to primary.
  const reorderRefs = useCallback((from: number, to: number) => {
    setRefs((prev) => {
      if (from === to || from < 0 || to < 0 || from >= prev.length || to >= prev.length) {
        return prev;
      }
      const next = prev.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  // Sequentially apply the given on-device post-processing steps to a generated
  // image, returning the final data URL and which step produced it. Reuses the
  // same `localOp` progress UI as the manual tools, and honours the genId guard
  // so a superseded run bails out.
  const applyPostProcessing = async (
    imageUrl: string,
    id: number,
    steps: LocalOpKind[],
  ): Promise<{ url: string; lastSource?: LocalOpKind }> => {
    let current = imageUrl;
    let lastSource: LocalOpKind | undefined;
    for (const kind of steps) {
      if (genIdRef.current !== id) break;
      setLocalOp({ kind, phase: isInstalled(kind) ? 'loading' : 'downloading', progress: null });
      const blob = await runEngineLocalOp(kind, current, {
        onProgress: (s) => {
          if (genIdRef.current === id) setLocalOp({ kind, phase: s.phase, progress: s.progress });
        },
      });
      if (genIdRef.current !== id) break;
      current = await blobToDataUrl(blob);
      markInstalled(kind);
      lastSource = kind;
    }
    return { url: current, lastSource };
  };

  // Auto-save a produced result to History (its image + the context needed to
  // remix it) and log the save. Best-effort — the hook swallows storage errors
  // so a failed save never disrupts the generation already on screen.
  const saveToHistory = useCallback(
    (
      out: MagicResult,
      ctx: {
        model: string;
        prompt: string;
        templateId: string;
        templateLabel: string;
        refs: MagicRef[];
      },
    ) => {
      void addGeneration({
        result: out,
        resolution: modelResolutions(ctx.model, activeProvider).length > 0 ? resolution : null,
        model: ctx.model,
        prompt: ctx.prompt,
        templateId: ctx.templateId,
        templateLabel: ctx.templateLabel,
        refs: ctx.refs,
      });
      assetLabLogger({
        action: 'Magic Studio',
        sub_action: 'Save Generation',
        extra: { text_1: ctx.model, text_2: out.source ?? 'generation', int_1: ctx.refs.length },
      });
    },
    [addGeneration, activeProvider, assetLabLogger, resolution],
  );

  const generate = () => {
    // Which path this generation takes (our backend / the user's key / demo) —
    // logged with the click and again with the result.
    const path = generationPath(activeModelId, { apiKey, selection });
    assetLabLogger({
      action: 'Magic Studio',
      sub_action: 'Generate',
      extra: { int_1: refs.length, text_1: activeModelId, text_2: path },
    });

    // Nothing to generate with: no key registered (backend path) or none
    // connected (direct path). Show the connect-a-key screen in the canvas —
    // silently doing nothing here is what makes "Generate does nothing" bugs.
    const missingCredentials = MAGIC_BACKEND_ENABLED ? !selection : !apiKey;
    if (missingCredentials) {
      if (MAGIC_BACKEND_ENABLED) {
        void message.warning('Add an API key and pick a model first.');
      }
      setResult(null);
      setGenError(null);
      setGenerating(false);
      setLocalOp(null);
      setComingSoon(true);
      return;
    }

    // The final prompt is the active template's system instruction plus the
    // user's content; "No template" contributes nothing, so the field is all.
    const activeTplObj = templates.find((t) => t.id === activeTpl);
    const finalPrompt = composePrompt(activeTplObj, prompt);
    if (!finalPrompt) {
      void message.warning('Add a prompt or pick a template first.');
      return;
    }
    // The backend requires at least 4 non-blank characters — catching it here
    // saves a round trip that would only come back as a 422.
    if (finalPrompt.trim().length < MIN_PROMPT_LENGTH) {
      void message.warning(`The prompt is too short — at least ${MIN_PROMPT_LENGTH} characters.`);
      return;
    }

    // Remember what the user wrote as soon as the run starts — the autocomplete
    // then offers it next time even if this generation fails. Only the field's
    // own content is kept, never the template's system instruction.
    setPromptHistory(rememberPrompt(userId, prompt));

    // On-device steps the active template asks to run on the result afterwards,
    // in order: upscale, then background removal.
    const postSteps: LocalOpKind[] = [];
    if (activeTplObj?.postUpscale) postSteps.push('upscale');
    if (activeTplObj?.postRemoveBg) postSteps.push('removeBg');

    // Snapshot the session context at click time so the saved history entry
    // reflects this generation even if the user edits the studio while it runs.
    const genCtx = {
      model: activeModelId,
      prompt,
      templateId: activeTpl,
      templateLabel: activeTplObj?.label ?? 'Custom',
      refs,
    };

    const id = (genIdRef.current += 1);
    setGenerating(true);
    setResult(null);
    setGenError(null);
    setComingSoon(false);
    setLocalOp(null);

    const provider = getProvider(activeModelId, { apiKey, selection });

    // Resolve the session's references into what the chosen path can send:
    //  • backend — `{ asset_id }` where the proxy can fetch the asset itself,
    //    inline base64 for uploads / non-raster / oversize originals;
    //  • direct  — base64 for everything, since the browser is the one calling
    //    the model. Either way the bytes travel through the app's authenticated
    //    client rather than a CORS-blocked plain fetch.
    const resolving = MAGIC_BACKEND_ENABLED
      ? resolveBackendRefs(refs).then(({ references, failed, oversize, compressed }) => ({
          images: [],
          references,
          failed,
          oversize,
          compressed,
        }))
      : resolveRefs(refs).then(({ images, failed, oversize }) => ({
          images,
          references: undefined,
          failed,
          oversize,
          // Direct path posts to Google, not through our ingress, so references
          // are never re-encoded to fit a body limit.
          compressed: [] as string[],
        }));

    void resolving
      .then(({ images, references, failed, oversize, compressed }) => {
        if (genIdRef.current !== id) return null; // superseded by a newer run / reopen
        if (failed > 0) {
          void message.warning(
            failed === 1
              ? 'One reference could not be loaded and was skipped.'
              : `${failed} references could not be loaded and were skipped.`,
          );
        }
        if (oversize.length > 0) {
          void message.warning(
            oversize.length === 1
              ? `“${oversize[0]}” is larger than 5 MB — a lower-resolution preview was used instead of the original.`
              : `${oversize.length} references are larger than 5 MB — their previews were used instead of the originals.`,
          );
        }
        // Re-encoding is lossy, so say it happened — the reference the model saw
        // is not byte-for-byte what the panel shows.
        if (compressed.length > 0) {
          void message.info(
            compressed.length === 1
              ? `“${compressed[0]}” was compressed to fit the request size limit.`
              : `${compressed.length} references were compressed to fit the request size limit.`,
          );
        }
        return provider.generate({
          modelId: activeModelId,
          prompt: finalPrompt,
          aspect,
          // Output size for Gemini; each provider drops it for a model that
          // doesn't offer the value (and OpenAI derives a canvas size from the
          // aspect instead).
          resolution,
          images,
          references,
        });
      })
      .then(async (out) => {
        if (!out) return;
        // The generation is charged against the key server-side, and the footer
        // shows what's left on it — re-read the keys so that figure moves now
        // instead of only when the modal is reopened. Done before the staleness
        // guard on purpose: the money is spent even if this run was superseded.
        if (MAGIC_BACKEND_ENABLED) refreshKeys();
        if (genIdRef.current !== id) return;
        setGenerating(false);
        // The cloud generation itself succeeded (post-processing, if any, is a
        // separate on-device step). Log the result with its token usage.
        assetLabLogger({
          action: 'Magic Studio',
          sub_action: 'Generate Result',
          extra: {
            text_1: 'Success',
            text_2: path,
            text_3: activeModelId,
            int_1: out.usage?.totalTokens ?? null,
            int_2: out.usage?.promptTokens ?? null,
            int_3: out.usage?.outputTokens ?? null,
          },
        });
        // Nothing to post-process — show the generation as-is and save it.
        if (postSteps.length === 0 || !out.imageUrl) {
          setResult(out);
          saveToHistory(out, genCtx);
          return;
        }
        try {
          const { url, lastSource } = await applyPostProcessing(out.imageUrl, id, postSteps);
          if (genIdRef.current !== id) return;
          setLocalOp(null);
          const finalResult =
            url === out.imageUrl
              ? out
              : {
                  ...out,
                  id: `gen_post_${Date.now()}`,
                  imageUrl: url,
                  source: lastSource,
                  bytes: dataUrlByteSize(url) ?? undefined,
                };
          setResult(finalResult);
          saveToHistory(finalResult, genCtx);
        } catch (err) {
          if (genIdRef.current !== id) return;
          setLocalOp(null);
          // Keep the generated image; just warn that post-processing failed.
          setResult(out);
          void message.warning(
            err instanceof Error ? err.message : 'Post-processing failed — showing the original.',
          );
        }
      })
      .catch((err: unknown) => {
        if (genIdRef.current !== id) return;
        setGenerating(false);
        assetLabLogger({
          action: 'Magic Studio',
          sub_action: 'Generate Result',
          extra: {
            text_1: 'Fail',
            text_2: path,
            text_3: activeModelId,
            int_1: null,
            int_2: null,
            int_3: null,
          },
        });
        // Surface the message in the canvas preview area rather than a transient
        // toast so the user can read the model's explanation and rephrase.
        setGenError(
          err instanceof MagicProviderError ? err.message : 'Generation failed. Please try again.',
        );
      });
  };

  const openHistory = () => {
    assetLabLogger({ action: 'Magic Studio', sub_action: 'View History' });
    setTab('history');
  };

  // A generation was saved into the Asset Lab (via the footer or a history
  // card's "Save to assets"): record the link on its history record, then reveal
  // it in the finder — close the studio, open the destination category and
  // select the new asset, exactly like a history card's cloud action.
  const handleGenerationSaved = useCallback(
    (id: string, link: SavedAssetLink) => {
      markGenerationSaved(id, link);
      dispatch(magicStudioActions.revealRequested(link));
    },
    [dispatch, markGenerationSaved],
  );

  // Restore aspect / resolution / model from a saved generation. setModel
  // re-resolves the resolution (see the model-change effect); the saved size is
  // valid for that model, so it survives.
  const applyOutputSettings = (item: MagicGenerationView) => {
    setAspect(item.aspect);
    if (item.resolution) setResolution(item.resolution);
    setModel(item.model);
    // Backend path: the record stores a technical model name, so move the
    // selection onto a key that can still serve it. Records from the direct path
    // (legacy ids) and models the user no longer has a key for simply find no
    // match — the current selection then stays as it is.
    if (MAGIC_BACKEND_ENABLED) {
      const match = modelOptions.find((o) => o.model === item.model);
      if (match) pickSelection(match);
    }
  };

  // "New from result": use the generated image itself as the sole reference for
  // a fresh generation, in the "Create with reference style" mode.
  const remixFromResult = (item: MagicGenerationView) => {
    assetLabLogger({
      action: 'Magic Studio',
      sub_action: 'Remix',
      extra: { text_1: 'new_from_result' },
    });
    if (item.imageBlob) {
      const url = URL.createObjectURL(item.imageBlob);
      objectUrlsRef.current.add(url);
      // Name it after what the stored bytes actually are — the ref badge and the
      // saved-asset name are both derived from this filename.
      setRefs([uploadRef(withImageExtension(`magic-${item.seed}.png`, item.imageBlob.type), url)]);
    } else {
      setRefs([]);
    }
    setPrompt(item.prompt);
    setActiveTpl(templates.some((t) => t.id === 'refstyle') ? 'refstyle' : CUSTOM_TEMPLATE_ID);
    applyOutputSettings(item);
    setResult(null);
    setGenError(null);
    setComingSoon(false);
    setTab('generate');
  };

  // "Reuse setup": reload the original references + prompt + settings so the
  // user can tweak and regenerate. Upload refs are rebuilt from their stored
  // bytes; asset refs from the persisted asset.
  const remixReuse = (item: MagicGenerationView) => {
    assetLabLogger({
      action: 'Magic Studio',
      sub_action: 'Remix',
      extra: { text_1: 'reuse_setup' },
    });
    const rebuilt = item.refs
      .map((r): MagicRef | null => {
        if (r.kind === 'asset' && r.asset) return assetRef(r.asset);
        if (r.kind === 'upload' && r.blob) {
          const url = URL.createObjectURL(r.blob);
          objectUrlsRef.current.add(url);
          return uploadRef(r.name, url);
        }
        return null;
      })
      .filter((r): r is MagicRef => r !== null);
    setRefs(rebuilt);
    setPrompt(item.prompt);
    setActiveTpl(
      templates.some((t) => t.id === item.templateId) ? item.templateId : CUSTOM_TEMPLATE_ID,
    );
    applyOutputSettings(item);
    setResult(null);
    setGenError(null);
    setComingSoon(false);
    setTab('generate');
  };

  const downloadGeneration = (item: MagicGenerationView) => {
    if (!item.imageUrl) return;
    void downloadImageUrl(
      item.imageUrl,
      `magic-${item.seed}.png`,
      aiMetaFor(item.model, item.source),
    );
  };

  const removeGeneration = (id: string) => {
    assetLabLogger({ action: 'Magic Studio', sub_action: 'Delete Generation' });
    deleteGeneration(id);
  };

  // The image an on-device tool operates on: the current result if there is one,
  // otherwise the primary reference (resolved to a data URL — asset bytes are
  // pulled through the authenticated client, exactly like generation does).
  const resolveLocalOpSource = async (): Promise<string> => {
    if (result?.imageUrl) return result.imageUrl;
    const primary = refs[0];
    if (!primary) throw new Error('Add a reference or generate an image first.');
    if (primary.kind === 'upload') {
      const blob = await (await fetch(primary.url)).blob();
      return blobToDataUrl(blob);
    }
    const { images } = await resolveRefs([primary]);
    if (!images.length) throw new Error('Could not load the reference image.');
    return `data:${images[0].mimeType};base64,${images[0].dataBase64}`;
  };

  // Run an on-device tool (background removal / upscale) on the canvas image and
  // show the processed result. Runs locally in a worker — no API key, no cost.
  const runLocalOp = (kind: LocalOpKind) => {
    assetLabLogger({
      action: 'Magic Studio',
      sub_action: kind === 'upscale' ? 'Upscale' : 'Remove Background',
    });

    const id = (genIdRef.current += 1);
    setComingSoon(false);
    setGenError(null);
    // Already-cached models load (don't re-download) — label the initial phase
    // accordingly so a cached run doesn't say "Downloading".
    setLocalOp({ kind, phase: isInstalled(kind) ? 'loading' : 'downloading', progress: null });

    void resolveLocalOpSource()
      .then((sourceUrl) => {
        if (genIdRef.current !== id) return null;
        return runEngineLocalOp(kind, sourceUrl, {
          onProgress: (s) => {
            if (genIdRef.current === id) setLocalOp({ kind, phase: s.phase, progress: s.progress });
          },
        });
      })
      .then((blob) => (blob && genIdRef.current === id ? blobToDataUrl(blob) : null))
      .then((imageUrl) => {
        if (!imageUrl || genIdRef.current !== id) return;
        markInstalled(kind);
        const opResult: MagicResult = {
          id: `local_${Date.now()}`,
          seed: 0,
          aspect,
          format: 'PNG',
          imageUrl,
          source: kind,
        };
        setResult(opResult);
        setLocalOp(null);
        // On-device tool output is a generation too — auto-save it.
        saveToHistory(opResult, {
          model: activeModelId,
          prompt,
          templateId: 'post',
          templateLabel: kind === 'upscale' ? 'Upscaled 2×' : 'Background removed',
          refs,
        });
      })
      .catch((err: unknown) => {
        if (genIdRef.current !== id) return;
        setLocalOp(null);
        setGenError(
          err instanceof Error ? err.message : 'On-device tool failed. Please try again.',
        );
      });
  };

  // Download the current result (generation or on-device). The extension of the
  // name below is a default — downloadImageUrl re-points it at the real format.
  const downloadResult = () => {
    if (!result?.imageUrl) return;
    const base = primaryAsset ? assetBaseName(primaryAsset) : 'magic-studio';
    const suffix =
      result.source === 'removeBg'
        ? '-no-bg'
        : result.source === 'upscale'
          ? '-upscaled'
          : `-${result.seed}`;
    void downloadImageUrl(
      result.imageUrl,
      `${base}${suffix}.png`,
      aiMetaFor(activeModelId, result.source),
    );
  };

  return (
    <Modal
      className="al-magic-modal"
      open={visible}
      width={1240}
      centered
      footer={null}
      closable={false}
      destroyOnClose
      onCancel={close}
    >
      <div className="al-magic-modal__shell">
        {/* Compact header — logo + title, the Generate | History tabs, and close.
            (The API key / model controls live in the prompt toolbar.) */}
        <div className="al-magic-modal__header">
          <div className="al-magic-modal__logo">
            <MagicIcon size={14} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: TM.text, lineHeight: 1.2 }}>
            Magic Studio
          </div>
          <div className="al-magic-modal__tabs">
            <StudioTab
              active={tab === 'generate'}
              icon={<MagicIcon size={13} />}
              label="Generate"
              onClick={() => setTab('generate')}
            />
            <StudioTab
              active={tab === 'history'}
              icon={<HistoryIcon size={13} />}
              label="History"
              badge={history.length}
              onClick={openHistory}
            />
          </div>
          <div style={{ flex: 1 }} />
          {/* Closing keeps the session, so clearing it needs a button of its own. */}
          <Popconfirm
            title="Start a new session?"
            description="Clears the references, prompt, output settings and the current result."
            okText="New session"
            cancelText="Keep it"
            placement="bottomRight"
            disabled={!sessionDirty}
            onConfirm={newSession}
          >
            {/* Span wrapper gives antd a ref-able node (MagicBtn forwards none). */}
            <span style={{ display: 'inline-flex' }}>
              <MagicBtn
                kind="secondary"
                size="sm"
                icon={<RefreshIcon size={12} />}
                disabled={!sessionDirty}
              >
                New session
              </MagicBtn>
            </span>
          </Popconfirm>
          <button
            type="button"
            className="al-magic-modal__close"
            onClick={close}
            aria-label="Close Magic Studio"
          >
            <CloseIcon size={14} />
          </button>
        </div>

        {/* Body: References · Canvas · Templates+Output, or the History gallery */}
        {tab === 'generate' ? (
          <div className="al-magic-modal__body">
            <RefsPanel
              refs={refs}
              primaryAssetId={primaryAsset?.id ?? null}
              onRemove={removeRef}
              onReorder={reorderRefs}
              onOpenPicker={() => setPicking(true)}
              onAddFiles={addFiles}
            />
            <CanvasPanel
              generating={generating}
              result={result}
              error={genError}
              comingSoon={comingSoon}
              localOp={localOp}
              primaryRef={refs[0] ?? null}
              prompt={prompt}
              setPrompt={setPrompt}
              promptHistory={promptHistory}
              onGenerate={generate}
              onResetTemplateToCustom={resetTemplateToCustom}
              aspect={aspect}
              activeTpl={activeTpl}
              templates={templates}
              apiKey={apiKey}
              onApiKeyChange={setDirectApiKey}
              model={model}
              onPickModel={setModel}
              keys={keys}
              keysLoading={keysLoading}
              keysError={keysError}
              onReloadKeys={reloadKeys}
              onAddKey={addKey}
              onRemoveKey={removeKey}
              modelOptions={modelOptions}
              selection={selection}
              onSelect={pickSelection}
              // Don't offer Generate while we still don't know what the user can
              // generate with — pressing it would only bounce off the gate.
              generateDisabled={keysLoading || (MAGIC_BACKEND_ENABLED && !selection)}
            />
            <TemplatesPanel
              templates={templates}
              activeTpl={activeTpl}
              onPickTemplate={pickTemplate}
              onStartEdit={(t) => setEditingTpl(t)}
              onAddTemplate={addTpl}
              onAddModifier={addModifier}
              aspect={aspect}
              setAspect={setAspect}
              // Legacy studio id on the direct path, technical model name on the
              // backend one; modelResolutions() understands both. The Resolution
              // dropdown hides itself only where there is no size choice at all
              // (OpenAI models).
              modelId={activeModelId}
              provider={activeProvider}
              resolution={resolution}
              setResolution={setResolution}
              onRunLocalOp={runLocalOp}
              canRunLocalOp={!!result?.imageUrl || !!refs[0]}
              localOpBusy={generating || !!localOp}
              localOpRunningKind={localOp?.kind ?? null}
            />
          </div>
        ) : (
          <MagicHistory
            history={history}
            onRemixNew={remixFromResult}
            onRemixReuse={remixReuse}
            onDownload={downloadGeneration}
            onDelete={removeGeneration}
            onReveal={(item) =>
              item.savedAsset && dispatch(magicStudioActions.revealRequested(item.savedAsset))
            }
            // A reference that came from the catalog can be opened where it
            // lives; the listener resolves its category, since the stored copy
            // may be a grid-card asset that never carried one.
            onOpenAsset={(assetId) =>
              dispatch(magicStudioActions.assetRevealRequested({ assetId }))
            }
            onSaved={handleGenerationSaved}
            onGoGenerate={() => setTab('generate')}
          />
        )}

        {/* Footer */}
        <div className="al-magic-modal__footer">
          <span style={{ fontSize: 12, color: TM.textMute }}>
            {tab === 'history' ? (
              <>
                <b style={{ color: TM.text }}>{history.length}</b> generation
                {history.length === 1 ? '' : 's'} saved on this device · pick one to iterate.
              </>
            ) : isRealGeneration({ apiKey, selection }) ? (
              <>
                Generations are saved to <b style={{ color: TM.text }}>History</b> on this device
                automatically.
                {selectedKeyRemaining != null && (
                  <>
                    {' · '}
                    <b style={{ color: TM.text }}>${selectedKeyRemaining.toFixed(2)}</b> left on
                    this key this month.
                  </>
                )}
              </>
            ) : MAGIC_BACKEND_ENABLED ? (
              <>
                <b style={{ color: TM.orange }}>No API key</b> — add your Gemini or OpenAI key to
                run generations.
              </>
            ) : (
              <>
                <b style={{ color: TM.orange }}>Demo mode</b> — connect your API key to run real
                generations.
              </>
            )}
          </span>
          <div style={{ flex: 1 }} />
          <MagicBtn kind="ghost" onClick={close}>
            Close
          </MagicBtn>
          {tab === 'generate' && (
            <>
              <SaveToAssetsButton
                imageUrl={result?.imageUrl ?? null}
                firstRef={refs[0] ?? null}
                model={activeModelId}
                source={result?.source}
                disabled={!result?.imageUrl}
                onSaved={(link) => result && handleGenerationSaved(result.id, link)}
              />
              <MagicBtn kind="secondary" disabled={!result?.imageUrl} onClick={downloadResult}>
                Download
              </MagicBtn>
            </>
          )}
        </div>
      </div>

      {picking && (
        <AssetPicker
          allAssets={allAssets}
          alreadyRefs={refs}
          onPick={addRef}
          onClose={() => setPicking(false)}
        />
      )}
      {editingTpl && (
        <TemplateEditor
          tpl={editingTpl}
          isNew={!editingExists}
          onSave={saveTpl}
          onCancel={() => setEditingTpl(null)}
          onDelete={
            editingTpl.custom && editingExists
              ? () => {
                  removeTpl(editingTpl.id);
                  setEditingTpl(null);
                }
              : null
          }
        />
      )}
    </Modal>
  );
};
